import type { ColumnFiltersState } from '@tanstack/react-table'
import type { FilterCondition } from '@/utils/filterUtils'

/**
 * Converts OpenStatus DataTableInfinite column filter state into the
 * legacy FilterCondition[] format understood by useCallsQuery.
 *
 * Called independently/additively — the result is merged with any legacy
 * FilterCondition[] from the old dropdown.
 *
 * NOTE: condition IDs are deterministic (`${field}-${operator}`) so this
 * function returns structurally-equal output across calls with the same
 * input. Random UUIDs would make `useCallsQuery`'s query key (which embeds
 * `params.filters`) change on every render, causing a refetch on every
 * row-selection change in the v2 table — see the row-click → empty-sheet
 * regression that motivated this.
 *
 * `containsQueryTransforms` rewrites the query of a text filter before it is
 * sent, keyed by field. It is a per-caller argument rather than a constant
 * here because the same field name means different things per table — six
 * tables share this converter, and each one's backend decides how its column
 * is matched (see `callsContainsQueryTransforms` for the Phone example).
 */
export function columnFiltersToFilterConditions(
  columnFilters: ColumnFiltersState,
  containsQueryTransforms: Record<string, (value: string) => string> = {}
): FilterCondition[] {
  const conditions: FilterCondition[] = []

  for (const filter of columnFilters) {
    const { id: field, value } = filter

    if (value === null || value === undefined) continue

    // Checkbox / multi-select array → "in" operator with comma-separated value
    if (Array.isArray(value)) {
      // Decide by content, not length: a two-element string selection (e.g.
      // ["ecw", "epic_csv"]) is a multi-value checkbox, not a range. Only a
      // numeric pair is a slider range, and any Date element is a timerange.
      const isNumericRange =
        value.length === 2 &&
        value.some((endpoint) => typeof endpoint === 'number') &&
        value.every(
          (endpoint) =>
            typeof endpoint === 'number' ||
            endpoint === null ||
            endpoint === undefined
        )

      if (isNumericRange) {
        // slider range: [min, max] — either endpoint may be absent
        const [start, end] = value
        if (typeof start === 'number') {
          conditions.push({
            id: `${field}->=`,
            field,
            operator: '>=',
            value: String(start),
          })
        }
        if (typeof end === 'number') {
          conditions.push({
            id: `${field}-<=`,
            field,
            operator: '<=',
            value: String(end),
          })
        }
      } else if (value.some((v) => v instanceof Date)) {
        // timerange: Date objects → ISO strings (start and/or end)
        const [start, end] = value
        if (start instanceof Date) {
          conditions.push({
            id: `${field}->=`,
            field,
            operator: '>=',
            value: start.toISOString(),
          })
        }
        if (end instanceof Date) {
          conditions.push({
            id: `${field}-<=`,
            field,
            operator: '<=',
            value: end.toISOString(),
          })
        }
      } else if (value.length > 0) {
        // Multi-value checkbox: ["inbound", "outbound"]
        const strValues = value
          .filter((v): v is string => typeof v === 'string')
          .map((v) => v.trim())
          .filter(Boolean)
        if (strValues.length > 0) {
          conditions.push({
            id: `${field}-in`,
            field,
            operator: 'in',
            value: strValues.join(','),
          })
        }
      }
      continue
    }

    // Input text filter
    if (typeof value === 'string' && value.trim()) {
      const trimmed = value.trim()
      // hasOwn, not a bare lookup: a field named like an Object.prototype key
      // ("constructor", "toString") would otherwise resolve to an inherited
      // function and be called as a transform.
      const transform = Object.hasOwn(containsQueryTransforms, field)
        ? containsQueryTransforms[field]
        : undefined
      conditions.push({
        id: `${field}-contains`,
        field,
        operator: 'contains',
        value: transform ? transform(trimmed) : trimmed,
      })
    }

    // Number filter (exact match)
    if (typeof value === 'number') {
      conditions.push({
        id: `${field}-=`,
        field,
        operator: '=',
        value: String(value),
      })
    }
  }

  return conditions
}
